import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useInstitutionalStudent } from "@/hooks/useInstitutionalStudent";

import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw, LogOut, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const LOADING_TIMEOUT = 9000; // 9s — between 8 and 10 as requested

export const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { isInstitutionalStudent, loading: institutionalLoading } = useInstitutionalStudent();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const hasActedRef = useRef(false);
  const toastShownRef = useRef(false);
  const [timedOut, setTimedOut] = useState(false);

  const allLoading = authLoading || adminLoading || institutionalLoading;
  const hasAccess = isAdmin;
  // Denial is decisive as soon as auth + admin check finished — don't wait
  // for institutional check (that one only affects redirect target).
  const authResolved = !authLoading && !adminLoading;
  // Email confirmation gate retired 2026-06 (auto_confirm_email=true).
  const denied = authResolved && !!user && !hasAccess;

  // Safety timeout — stop showing the spinner indefinitely.
  useEffect(() => {
    if (!allLoading) {
      setTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setTimedOut(true), LOADING_TIMEOUT);
    return () => clearTimeout(timer);
  }, [allLoading]);

  // Reset guards on user change
  useEffect(() => {
    hasActedRef.current = false;
    toastShownRef.current = false;
  }, [user?.id]);

  // Diagnostic logs (single emission per state change)
  const lastLogRef = useRef("");
  useEffect(() => {
    const key = `${location.pathname}|${authLoading}|${adminLoading}|${institutionalLoading}|${user?.id ?? "none"}|${isAdmin}|${isInstitutionalStudent}`;
    if (key === lastLogRef.current) return;
    lastLogRef.current = key;
    console.log("[AdminRoute] state", {
      route: location.pathname,
      authStatus: user ? "authenticated" : authLoading ? "loading" : "guest",
      userId: user?.id ?? null,
      isAdmin,
      isInstitutionalStudent,
      loading: { authLoading, adminLoading, institutionalLoading },
    });
  }, [location.pathname, authLoading, adminLoading, institutionalLoading, user?.id, isAdmin, isInstitutionalStudent]);

  // Routing decisions
  useEffect(() => {
    // Guest → login (don't wait for admin/institutional checks)
    if (!authLoading && !user) {
      if (hasActedRef.current) return;
      hasActedRef.current = true;
      console.log("[AdminRoute] redirect → /auth/login (guest)");
      navigate("/auth/login", { replace: true });
      return;
    }

    // Denied — wait for institutional check so we know the right destination,
    // but emit the toast only once.
    if (denied && !institutionalLoading && !hasActedRef.current) {
      hasActedRef.current = true;
      const target = "/dashboard";
      console.log("[AdminRoute] access denied", {
        userId: user?.id,
        isAdmin,
        isInstitutionalStudent,
        redirectTo: target,
        reason: "user_is_not_admin",
      });
      if (!isInstitutionalStudent && !toastShownRef.current) {
        toastShownRef.current = true;
        toast.error(t("admin.accessDenied"), { id: "admin-access-denied" });
      }
      navigate(target, { replace: true });
    }
  }, [authLoading, institutionalLoading, denied, user, isAdmin, isInstitutionalStudent, navigate, t]);

  // ── Denied fallback (shown briefly while navigation happens, or if redirect
  //     is blocked for any reason). Never get stuck on the spinner. ──
  if (denied) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
            <ShieldAlert className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">
            {t("admin.accessDenied")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("admin.adminOnly") || "Only administrators can access this section."}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <Button onClick={() => navigate("/dashboard", { replace: true })}>
              {t("subscription.backToDashboard") || "Go to dashboard"}
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                try { await supabase.auth.signOut({ scope: "global" }); } catch {}
                window.location.replace("/auth/login");
              }}
              className="gap-2"
            >
              <LogOut className="w-4 h-4" />
              {t("nav.logout") || "Log out"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (allLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t("admin.verifyingPermissions")}</p>
          {timedOut && (
            <>
              <p className="text-xs text-destructive mt-3">
                {t("common.loadTimeout") || "This is taking longer than expected."}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => window.location.reload()}
                >
                  <RefreshCw className="w-4 h-4" />
                  {t("common.retry") || "Reintentar"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={async () => {
                    try { await supabase.auth.signOut({ scope: "global" }); } catch {}
                    window.location.replace("/auth/login");
                  }}
                >
                  <LogOut className="w-4 h-4" />
                  {t("nav.logout") || "Salir"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (user && hasAccess) ? <>{children}</> : null;
};
